import * as Actions from './actions'
import ActionType from './action-type'
import {Api} from '../../../api/Api'
import {REST} from '../../../api'
import {type ThunkAction} from 'redux-thunk'
import {type AppState} from '../../types/app-state'
import {
    DEFAULT_NETWORK_ASSETS_FILTER_STATE,
    type DetailExpandedType,
    type FilterExpandedType,
    type SortColumnType,
    TimestampFilterType,
} from './state'
import {NetworkType} from '../../../pages/network-assets/context/types/network-type'
import {AssetNetworkType} from '../../../pages/network-assets/context/types/aggregation-state'
import {
    DEFAULT_NETWORK_ASSETS_FILTER,
    type NetworkAssetsFilter,
    type NetworkAssetsFilterForUserPref,
} from '../../../values/user-preferences/network-assets-filter'
import {type SavedNetworkAssetsFilter} from '../saved-filters/state'
import {formattedLocationsBlockSet, populateLocationsForFilterCriteria} from '../../../utils/Utils'
import {type AnyAction} from 'redux'
import {setCurrentAppliedSavedFilter} from '../saved-filters/action-creators'
import {formatNetworkLabel} from '../../../pages/network-assets/context/helpers'
import {type GuidType} from '../../../values/generic-type-definitions'
import {BlockStatus} from '../../../pages/network-assets/context/types/aggregation-state'

const USER_PREFS_URL = '/api/v1/userPreferences/unknown_assets_settings_v2'

interface UserPrefsResponse {
    data: {
        value: NetworkAssetsFilterForUserPref
    }
}

interface DefaultTagResponse {
    data: {
        value: {
            default_tag: string[]
        }
    }
}

async function getUserPreferences(): Promise<UserPrefsResponse> {
    return REST.get(USER_PREFS_URL)
}

async function getDefaultTag(): Promise<DefaultTagResponse> {
    return REST.get(`${USER_PREFS_URL}.default_tag`)
}

async function deleteDefaultTag(): Promise<void> {
    return REST.delete(`${USER_PREFS_URL}.default_tag`)
}

function createFilterFromUserPrefs(
    userPrefs: NetworkAssetsFilterForUserPref,
    defaultTag: string[],
    savedFilter?: SavedNetworkAssetsFilter,
): NetworkAssetsFilter {
    const baseFilter = savedFilter
        ? {
              ...(savedFilter.criteria as NetworkAssetsFilter),
              networks: savedFilter.criteria.networks?.map((network) =>
                  formatNetworkLabel(network),
              ),
              behaviours: savedFilter.criteria.behaviours,
          }
        : DEFAULT_NETWORK_ASSETS_FILTER

    return {
        ...baseFilter,
        selectedVessels: formattedLocationsBlockSet(userPrefs.locations),
        searchVesselTagTerm: defaultTag,
        searchVesselNameTerm: userPrefs.searchVesselNameTerm,
    }
}

function dispatchBulkAssignSavedFilter(
    dispatch: (
        action: Actions.Action | ThunkAction<Promise<void>, AppState, Api, Actions.Action>,
    ) => void,
    filter: NetworkAssetsFilter,
    filterName: string,
    detailExpanded: DetailExpandedType,
    isDefaultTagUser: boolean,
    savedFilter?: SavedNetworkAssetsFilter,
): void {
    dispatch(
        bulkAssignSavedFilter(filter, filterName, detailExpanded, isDefaultTagUser, savedFilter),
    )
}

export const fetchNetworkAssetsFilter = (
    savedFilters: SavedNetworkAssetsFilter[],
    isDefaultTagUser?: boolean,
): ThunkAction<Promise<void>, AppState, Api, Actions.Action> => {
    return async (dispatch) => {
        dispatch(requestFilter())

        try {
            if (isDefaultTagUser === true) {
                const [userPrefsResponse, defaultTagResponse] = await Promise.all([
                    getUserPreferences(),
                    getDefaultTag(),
                ])

                const userPrefs = userPrefsResponse.data.value
                const defaultTag = defaultTagResponse.data.value.default_tag

                if (!userPrefs.selectedFilterName) {
                    const filter = createFilterFromUserPrefs(userPrefs, defaultTag)
                    dispatchBulkAssignSavedFilter(
                        dispatch,
                        filter,
                        'Saved filters',
                        userPrefs.detailExpanded,
                        true,
                    )
                } else {
                    const savedFilter = savedFilters.find(
                        (filter) => filter.name === userPrefs.selectedFilterName,
                    )

                    if (savedFilter) {
                        const filter = createFilterFromUserPrefs(userPrefs, defaultTag, savedFilter)
                        dispatchBulkAssignSavedFilter(
                            dispatch,
                            filter,
                            savedFilter.name,
                            userPrefs.detailExpanded,
                            true,
                            savedFilter,
                        )
                    } else {
                        const filter = createFilterFromUserPrefs(userPrefs, defaultTag)
                        dispatchBulkAssignSavedFilter(
                            dispatch,
                            filter,
                            'Saved filters',
                            userPrefs.detailExpanded,
                            true,
                        )
                    }
                }

                await deleteDefaultTag()
            } else {
                try {
                    const [userPrefsResponse, defaultTagResponse] = await Promise.all([
                        getUserPreferences(),
                        getDefaultTag(),
                    ])

                    const userPrefs = userPrefsResponse.data.value
                    const defaultTag = defaultTagResponse.data.value.default_tag

                    if (!userPrefs.selectedFilterName) {
                        const filter = createFilterFromUserPrefs(userPrefs, defaultTag)
                        dispatchBulkAssignSavedFilter(
                            dispatch,
                            filter,
                            'Saved filters',
                            userPrefs.detailExpanded,
                            false,
                        )
                    } else {
                        const savedFilter = savedFilters.find(
                            (filter) => filter.name === userPrefs.selectedFilterName,
                        )

                        if (savedFilter) {
                            const filter = createFilterFromUserPrefs(
                                userPrefs,
                                defaultTag,
                                savedFilter,
                            )
                            dispatchBulkAssignSavedFilter(
                                dispatch,
                                filter,
                                savedFilter.name,
                                userPrefs.detailExpanded,
                                false,
                                savedFilter,
                            )
                        } else {
                            const filter = createFilterFromUserPrefs(userPrefs, defaultTag)
                            dispatchBulkAssignSavedFilter(
                                dispatch,
                                filter,
                                'Saved filters',
                                userPrefs.detailExpanded,
                                false,
                            )
                        }
                    }
                } catch {
                    const defaultTagResponse = await getDefaultTag()
                    const defaultTag = defaultTagResponse.data.value.default_tag

                    await REST.put(USER_PREFS_URL, {
                        ...DEFAULT_NETWORK_ASSETS_FILTER_STATE,
                        searchVesselTagTerm: defaultTag,
                    })

                    dispatchBulkAssignSavedFilter(
                        dispatch,
                        {
                            ...DEFAULT_NETWORK_ASSETS_FILTER,
                            searchVesselTagTerm: defaultTag,
                        },
                        'Saved filters',
                        DEFAULT_NETWORK_ASSETS_FILTER.detailExpanded,
                        false,
                    )
                }
            }
        } catch {
            dispatchBulkAssignSavedFilter(
                dispatch,
                DEFAULT_NETWORK_ASSETS_FILTER,
                'Saved filters',
                DEFAULT_NETWORK_ASSETS_FILTER.detailExpanded,
                false,
            )
        }
    }
}

function requestFilter(): Actions.RequestFilterAction {
    return {
        type: ActionType.REQUEST_FILTER,
    }
}

export function setSortColumn(
    sortColumn: SortColumnType,
): ThunkAction<void, AppState, Api, Actions.SetSortColumnAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.SET_SORT_COLUMN,
            payload: sortColumn,
        })
    }
}

export function setSearchHostname(
    hostname: string,
): ThunkAction<void, AppState, Api, Actions.SetSearchedHostnameAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.SET_SEARCHED_HOSTNAME,
            payload: hostname,
        })
    }
}

export function setSearchProperties(
    properties: string,
): ThunkAction<void, AppState, Api, Actions.SetSearchedPropertiesAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.SET_SEARCHED_PROPERTIES,
            payload: properties,
        })
    }
}

export function setSearchIpFilter(
    ipFilter: string,
): ThunkAction<void, AppState, Api, Actions.SetSearchedIpAddressAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.SET_SEARCHED_IP_ADDRESS,
            payload: ipFilter,
        })
    }
}

export function setSearchNicVendor(
    nicVendor: string,
): ThunkAction<void, AppState, Api, Actions.SetSearchedMacDescriptionAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.SET_SEARCHED_MAC_DESCRIPTION,
            payload: nicVendor,
        })
    }
}

export function setSearchedLastSeen(
    selectedTimeRange: TimestampFilterType | undefined,
): ThunkAction<void, AppState, Api, Actions.SetSearchedTimeRange> {
    return (dispatch) => {
        dispatch({
            type: ActionType.SET_SEARCHED_LAST_ACTIVE,
            payload: selectedTimeRange,
        })
    }
}

export function toggleNetworkAssetState(
    allNetworkAssetState: BlockStatus[],
    selectedNetworkAssetBlockStatus: BlockStatus,
    networkAssetStateNewValue: boolean,
): ThunkAction<void, AppState, Api, Actions.ToggleNetworkAssetStateAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.TOGGLE_NETWORK_ASSET_STATE,
            payload: {
                allNetworkAssetState,
                selectedNetworkAssetBlockStatus,
                networkAssetStateNewValue,
            },
        })
    }
}

export function toggleAllNetworkAssetState(
    selected: boolean,
): ThunkAction<void, AppState, Api, Actions.ToggleAllNetworkAssetStateAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.TOGGLE_ALL_NETWORK_ASSET_STATE,
            payload: selected,
        })
    }
}

export function toggleNetwork(
    allNetworks: NetworkType[],
    selectedNetwork: NetworkType,
    networkNewValue: boolean,
): ThunkAction<void, AppState, Api, Actions.ToggleNetworkAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.TOGGLE_NETWORK,
            payload: {allNetworks, selectedNetwork, networkNewValue},
        })
    }
}

export function toggleAllNetworks(
    selected: boolean,
): ThunkAction<void, AppState, Api, Actions.ToggleAllNetworksAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.TOGGLE_ALL_NETWORKS,
            payload: selected,
        })
    }
}

export function toggleBehaviour(
    allBehaviours: GuidType[],
    selectedBehaviour: GuidType,
    behaviourNewValue: boolean,
): ThunkAction<void, AppState, Api, Actions.ToggleBehaviourAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.TOGGLE_BEHAVIOUR,
            payload: {
                allBehaviours,
                selectedBehaviour,
                behaviourNewValue,
            },
        })
    }
}

export function toggleAllBehaviours(
    selected: boolean,
): ThunkAction<void, AppState, Api, Actions.ToggleAllBehavioursAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.TOGGLE_ALL_BEHAVIOURS,
            payload: selected,
        })
    }
}

export function selectBehaviour(
    allBehaviours: GuidType[],
    selectedBehaviour: GuidType,
    behaviourNewValue: boolean,
): ThunkAction<void, AppState, Api, Actions.SelectBehaviourAction> {
    return (dispatch) => {
        dispatch({
            type: ActionType.SELECT_BEHAVIOUR,
            payload: {
                allBehaviours,
                selectedBehaviour,
                behaviourNewValue,
            },
        })
    }
}

export function setOtPseudoCriteria(payload: {
    otBehaviours?: GuidType[]
    otNetworks?: AssetNetworkType[]
    applyOtCriteria?: boolean
    otLink?: boolean
}): ThunkAction<void, AppState, Api, Actions.SetOtPseudoCriteriaAction> {
    return (dispatch) => {
        dispatch({type: ActionType.SET_OT_PSEUDO_CRITERIA, payload})
    }
}

export function clearOtPseudoCriteria(): ThunkAction<
    void,
    AppState,
    Api,
    Actions.ClearOtPseudoCriteriaAction
> {
    return (dispatch) => {
        dispatch({type: ActionType.CLEAR_OT_PSEUDO_CRITERIA})
    }
}

export function toggleApplyOtCriteria(): ThunkAction<
    void,
    AppState,
    Api,
    Actions.ToggleApplyOtCriteriaAction
> {
    return (dispatch) => {
        dispatch({type: ActionType.TOGGLE_APPLY_OT_CRITERIA})
    }
}

export function toggleFilterExpanded(
    filterExpanded: FilterExpandedType,
): ThunkAction<Promise<void>, AppState, Api, Actions.ToggleFilterExpandedAction> {
    return async (dispatch, getState) => {
        const currentFilter = getState().networkAssetsFilter
        const currentVesselFilter = getState().vesselFilter
        dispatch({
            type: ActionType.TOGGLE_FILTER_EXPANDED,
            payload: filterExpanded,
        })
        await REST.put(USER_PREFS_URL, {
            selectedFilterName: currentFilter.selectedFilterName,
            locations: currentVesselFilter.locations,
            searchVesselTagTerm: currentVesselFilter.searchVesselTagTerm,
            searchVesselNameTerm: currentVesselFilter.searchVesselNameTerm,
            detailExpanded: currentFilter.detailExpanded,
            filterExpanded,
        })
    }
}

export function toggleModalDetailsExpanded(
    detailExpanded: DetailExpandedType,
): ThunkAction<Promise<void>, AppState, Api, Actions.ToggleDetailExpandedAction> {
    return async (dispatch, getState) => {
        const currentFilter = getState().networkAssetsFilter
        dispatch({
            type: ActionType.TOGGLE_DETAIL_EXPANDED,
            payload: detailExpanded,
        })
        await REST.put(USER_PREFS_URL, {
            selectedFilterName: currentFilter.selectedFilterName,
            detailExpanded,
            filterExpanded: currentFilter.filterExpanded,
        })
    }
}

export function resetFilter(): ThunkAction<Promise<void>, AppState, Api, AnyAction> {
    return async (dispatch, getState) => {
        const response = await getUserPreferences()
        await dispatch(toggleModalDetailsExpanded(response.data.value.detailExpanded))

        const currentFilter = getState().networkAssetsFilter
        dispatch({type: ActionType.RESET_FILTER})
        dispatch(setCurrentAppliedSavedFilter(undefined))

        await REST.put(USER_PREFS_URL, {
            selectedFilterName: '',
            searchVesselTagTerm: [],
            searchVesselNameTerm: '',
            detailExpanded: currentFilter.detailExpanded,
        })
    }
}

export function bulkAssignSavedFilter(
    data: NetworkAssetsFilter,
    filterName: string,
    detailExpanded: DetailExpandedType,
    pref?: boolean,
    savedFilter?: SavedNetworkAssetsFilter,
): ThunkAction<Promise<void>, AppState, Api, AnyAction> {
    return async (dispatch, getState) => {
        const currentVesselFilter = getState().vesselFilter
        dispatch({
            type: ActionType.BULK_ASSIGN_SAVED_FILTER,
            payload: {
                criteria: data,
                name: filterName,
                detailExpanded: detailExpanded,
            },
        })
        if (savedFilter) {
            dispatch(setCurrentAppliedSavedFilter(savedFilter))
        }
        if (pref) {
            await REST.put(USER_PREFS_URL, {
                selectedFilterName: filterName,
                locations: populateLocationsForFilterCriteria(currentVesselFilter.locations),
                searchVesselTagTerm: currentVesselFilter.searchVesselTagTerm,
                searchVesselNameTerm: currentVesselFilter.searchVesselNameTerm,
                detailExpanded: detailExpanded,
            })
        }
    }
}

export function setSelectedFilterName(
    filterName: string,
    pref?: {
        savedFilter: SavedNetworkAssetsFilter
        detailExpanded: DetailExpandedType
    },
): ThunkAction<Promise<void>, AppState, Api, AnyAction> {
    return async (dispatch, getState) => {
        const currentVesselFilter = getState().vesselFilter
        dispatch({
            type: ActionType.SET_SELECTED_FILTER_NAME,
            payload: filterName,
        })
        if (pref) {
            dispatch(setCurrentAppliedSavedFilter(pref.savedFilter))
            await REST.put(USER_PREFS_URL, {
                selectedFilterName: filterName,
                detailExpanded: pref.detailExpanded,
                locations: populateLocationsForFilterCriteria(currentVesselFilter.locations),
                searchVesselTagTerm: currentVesselFilter.searchVesselTagTerm,
                searchVesselNameTerm: currentVesselFilter.searchVesselNameTerm,
            })
        }
    }
}

export function setSkipSettingFilter(data: {fromRelativeLastSeen: boolean}) {
    return {
        type: ActionType.SET_SKIP_SETTING_FILTER,
        payload: data,
    }
}

export function setMonitoredOnly(value: boolean) {
    return {
        type: ActionType.SET_MONITORED_ONLY,
        payload: value,
    }
}
export function setStateMonitored(state: BlockStatus, value: boolean) {
    return {
        type: ActionType.SET_STATE_MONITORED,
        payload: {state, value},
    }
}
